import SubScriptionManagerBase from "./subscriptionmanagerbase";
import AuthenticationAuthorizationError from "../utilities/errors/authenticationAuthorizationError";
import {
  EErrorOrigin,
  EErrorType,
  EValidateSubscriptionTokenErrorCode,
} from "./../../common/utilities/enums";
import { isAuthenticationAuthorizationError } from "../utilities/errors/authenticationAuthorizationError";
import { AUTHENTICATED } from "./../../common/analytics/analyticsevents";

// this class will implement al the necessary API communication
// and cookie management to authenticate users and authorize
// downloads. It does not implement any UI elements.
export default class SubScriptionManagerSSO extends SubScriptionManagerBase {
  constructor(
    appState,
    sessionManager,
    apiManager,
    cookieManager,
    options = {}
  ) {
    super(appState, sessionManager, apiManager, cookieManager, options);
  }

  // irrelevant for the SSO subscriptionmanager, will reject always
  // eslint-disable-next-line
  authenticateByUserNamePassword(username, password) {
    return Promise.reject("not allowed in sso");
  }

  getAuthenticationDataFromUrl = () => {
    const tokenFromUrl = this.getURLParameterValue(
      this._options.urlSSOTokenParameter
    );
    if (tokenFromUrl) {
      return {
        token: tokenFromUrl,
      };
    }
    return;
  };

  getAuthenticationDataFromCookie = () => {
    const rawCookie = this._cookieManager.getCookie(
      this._options.SSOCookieName
    );
    this.removeSSOCookie();
    if (!rawCookie) {
      return;
    }
    try {
      return JSON.parse(rawCookie);
    } catch (e) {
      return rawCookie;
    }
  };

  getAuthenticationData = () => {
    let authenticationData;

    if (this._options.getSSOTokenFromUrl) {
      authenticationData = this.getAuthenticationDataFromUrl();
    }

    if (!authenticationData) {
      authenticationData = this.getAuthenticationDataFromCookie();
    }

    return authenticationData;
  };

  getToken = () => {
    let token;
    const authenticationData = this.getAuthenticationData();

    if (authenticationData) {
      if (authenticationData.token) {
        //client like lmo, their sso cookie contains an object with a property token
        token = authenticationData.token.toString();
      } else {
        //client like dmg, their sso cookie contains a string as their token
        token = authenticationData.toString();
      }
      // client like lux, their sso cookie contains an object with properties token and hash
      if (authenticationData.hash) {
        token = `${authenticationData.hash}|${token}`;
      }
    }
    return token;
  };

  validateAuthToken = (ssoToken, downloadToken) => {
    const validateAuthTokenPromise = new Promise((resolve, reject) => {
      this._sessionManager
        .getSessionInfo()
        .then((sessionId) => {
          const userId = this._sessionManager.userId;
          const subsType =
            this._options.subscriptionOptions.subscriptionProvider;
          this._apiManager
            .validateSubscriptionToken(
              userId,
              sessionId,
              ssoToken,
              downloadToken,
              subsType
            )
            .then((response) => {
              resolve(response);
            })
            .catch((error) => {
              this.removeSSOCookie(true);
              reject(
                new AuthenticationAuthorizationError(
                  error,
                  EErrorOrigin.VALIDATESUBSCRIPTIONTOKEN,
                  EValidateSubscriptionTokenErrorCode.CALL_FAILED
                )
              );
            });
        })
        .catch((error) => {
          this.removeSSOCookie(true);
          if (error && isAuthenticationAuthorizationError(error)) {
            reject(error);
          }
          reject("no valid session");
        });
    });
    return validateAuthTokenPromise;
  };

  handleValidateAuthTokenFailed = (authTokenValidationResponse, reject) => {
    if (typeof authTokenValidationResponse.StatusMessage !== "string") {
      reject(
        new AuthenticationAuthorizationError(
          authTokenValidationResponse,
          EErrorOrigin.VALIDATESUBSCRIPTIONTOKEN,
          EValidateSubscriptionTokenErrorCode.STATUS_NOT_A_STRING,
          EErrorType.AUTHENTICATION
        )
      );
    } else {
      reject(
        new AuthenticationAuthorizationError(
          authTokenValidationResponse,
          EErrorOrigin.VALIDATESUBSCRIPTIONTOKEN,
          EValidateSubscriptionTokenErrorCode.UNSUCCESSFULL,
          EErrorType.AUTHENTICATION
        )
      );
    }
  };

  authenticate = (downloadToken) => {
    this.downloadToken = downloadToken;
    const authenticationPromise = new Promise((resolve, reject) => {
      const authToken = this.getToken();

      if (authToken) {
        this.validateAuthToken(authToken, downloadToken)
          .then((response) => {
            if (response) {
              if (response.Status.toLowerCase() === "success") {
                this._authenticatedSuccess(response);
                this._appState.triggerEvent(AUTHENTICATED);
                resolve(response);
              } else {
                this.removeSSOCookie(true);
                this.handleValidateAuthTokenFailed(response, reject);
              }
            }
          })
          .catch((error) => {
            reject(error);
          });
      } else {
        reject("no sso cookie");
      }
    });
    return authenticationPromise;
  };

  getURLParameterValue(parameter) {
    const urlQuery = window.location.search.substring(1);
    if (!urlQuery || urlQuery === "") {
      return;
    }

    const parameters = urlQuery.split("&");

    for (let i = 0; i < parameters.length; i++) {
      const parameterKeyValue = parameters[i].split("=");
      const key = parameterKeyValue[0];
      const value = parameterKeyValue[1];

      if (key === parameter) {
        return value;
      }
    }
    return;
  }

  removeSSOCookie(force = false) {
    if (force || this._options.deleteSSOCookie) {
      this._cookieManager.removeCookie(
        this._options.SSOCookieName,
        this._options.SSOCookieOptions
      );
    }
  }

  _authenticatedSuccess(data) {
    // store subscriptionCookie
    this._createSubscriptionCookie(
      data.SubscriptionID,
      data.SubscriptionReference,
      data.SubscriptionUserReference,
      data.SubscriptionType
    );
    this.trigger("subscriptionmanager:authenticationstatuschanged");
  }
}



// WEBPACK FOOTER //
// ./src/common/subscriptionmanagement/subscriptionmanagersso.js